import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  ThemeIcon,
  SimpleGrid,
  Button,
  Group,
  Paper,
  Stack,
  Badge,
  Divider,
} from '@mantine/core';
import { Loader } from '../../components/common/Loader';
import {
  IconPackage,
  IconUsers,
  IconFileText,
  IconCertificate,
  IconArrowRight,
  IconPhoto,
  IconSettings,
  IconUserPlus,
  IconEye,
  IconMessageCircle,
} from '@tabler/icons-react';
import { Helmet } from 'react-helmet-async';
import { useAppSelector } from '../../store/hooks';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';


const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface RecentActivityItem {
  id: string;
  action: string;
  user: string;
  time: string;
  type: string;
}

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { content } = useAppSelector((state) => state.content);
  const { user, token } = useAppSelector((state) => state.auth);

  const [recentActivities, setRecentActivities] = useState<RecentActivityItem[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [unreadInquiries, setUnreadInquiries] = useState(0);

  // Stats data from content (static but dynamic via Redux)
  const stats = [
    { 
      title: 'Total Products', 
      value: content?.pages?.products?.products?.length || 0, 
      icon: IconPackage, 
      link: '/admin/gallery/products',
      color: 'blue',
      change: '+12%'
    },
    { 
      title: 'Categories', 
      value: content?.pages?.products?.categories?.length || 0, 
      icon: IconFileText, 
      link: '/admin/gallery/products',
      color: 'green',
      change: '+5%'
    },
    { 
      title: 'Certifications', 
      value: content?.pages?.certifications?.certifications?.length || 0, 
      icon: IconCertificate, 
      link: '/admin/gallery/certifications',
      color: 'violet',
      change: '+8%'
    },
    { 
      title: 'Countries', 
      value: content?.pages?.international?.countries?.length || 0, 
      icon: IconUsers, 
      link: '/admin/gallery/international',
      color: 'orange',
      change: '+15%'
    },
  ];

  // Fetch recent activity (inquiries and user creations)
  useEffect(() => {
    fetchRecentActivity();
    fetchUnreadInquiriesCount();
  }, []);

  const fetchRecentActivity = async () => {
    setLoadingActivities(true);
    try {
      // Fetch latest inquiries (limit 5)
      const inquiriesRes = await axios.get(`${API_URL}/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inquiries = inquiriesRes.data.slice(0, 3).map((inq: any) => ({
        id: inq.id,
        action: `New inquiry from ${inq.name}`,
        user: inq.email,
        time: new Date(inq.createdAt).toLocaleString(),
        type: 'inquiry'
      }));

      // Fetch latest users (limit 5)
      const usersRes = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = usersRes.data.slice(0, 2).map((usr: any) => ({
        id: usr.id,
        action: `User created: ${usr.fullName}`,
        user: usr.email,
        time: new Date(usr.createdAt).toLocaleString(),
        type: 'user'
      }));

      // Combine and sort by date (most recent first)
      const combined = [...inquiries, ...users].sort((a, b) => 
        new Date(b.time).getTime() - new Date(a.time).getTime()
      ).slice(0, 5);

      setRecentActivities(combined);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // Fallback to empty array
      setRecentActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchUnreadInquiriesCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/inquiries/unread/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadInquiries(res.data.count);
    } catch (error) {
      console.error('Error fetching unread inquiries count:', error);
    }
  };

  const getActivityIcon = (type: string) => {
    switch(type) {
      case 'user': return <IconUserPlus size={16} />;
      case 'product': return <IconPackage size={16} />;
      case 'gallery': return <IconPhoto size={16} />;
      case 'inquiry': return <IconMessageCircle size={16} />;
      default: return <IconEye size={16} />;
    }
  };

  const formatTime = (timeStr: string) => {
    const date = new Date(timeStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Thalvora Exports Admin</title>
      </Helmet>

      <Container size="xl">
        {/* Welcome Section */}
        <Group justify="space-between" mb="xl">
          <div>
            <Title order={1}>Welcome back, {user?.fullName || 'Admin'}!</Title>
            <Text c="dimmed" size="sm">
              Here's what's happening with your store today.
            </Text>
          </div>
          <Button 
            color="exportGreen" 
            onClick={() => navigate('/admin/users')}
            leftSection={<IconUserPlus size={16} />}
          >
            Users
          </Button>
        </Group>

        {/* Stats Grid */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mb="xl">
          {stats.map((stat) => (
            <Card 
              key={stat.title} 
              withBorder 
              padding="xl" 
              radius="md"
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
              onClick={() => navigate(stat.link)}
            >
              <Group justify="space-between" mb="md">
                <ThemeIcon
                  size="xl"
                  radius="md"
                  variant="light"
                  color={stat.color}
                >
                  <stat.icon size={24} />
                </ThemeIcon>
                <Badge color={stat.color} variant="light">
                  {stat.change}
                </Badge>
              </Group>
              
              <Text fz="xs" tt="uppercase" fw={700} c="dimmed">
                {stat.title}
              </Text>
              <Text fz="lg" fw={600}>
                {stat.value}
              </Text>
            </Card>
          ))}
        </SimpleGrid>

        {/* Main Content Grid */}
        <Grid>
          {/* Recent Activity */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Paper withBorder p="xl" radius="md">
              <Group justify="space-between" mb="md">
                <Title order={3}>Recent Activity</Title>
                <Button 
                  variant="subtle" 
                  color="exportGreen" 
                  size="xs"
                  onClick={() => navigate('/admin/inquiries')}
                >
                  View All Inquiries
                </Button>
              </Group>
              
              {loadingActivities ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : recentActivities.length === 0 ? (
                <Text c="dimmed" ta="center" py={8}>
                  No recent activity.
                </Text>
              ) : (
                <Stack>
                  {recentActivities.map((activity) => (
                    <Group key={activity.id} justify="space-between" className="py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <Group>
                        <ThemeIcon size="sm" variant="light" color="exportGreen">
                          {getActivityIcon(activity.type)}
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={500}>{activity.action}</Text>
                          <Text size="xs" c="dimmed">{activity.user}</Text>
                        </div>
                      </Group>
                      <Text size="xs" c="dimmed">{formatTime(activity.time)}</Text>
                    </Group>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid.Col>

          {/* Quick Actions */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Paper withBorder p="xl" radius="md">
              <Title order={3} mb="lg">Quick Actions</Title>
              <Stack>
                <Button 
                  variant="light" 
                  color="exportGreen" 
                  onClick={() => navigate('/admin/users')}
                  rightSection={<IconArrowRight size={16} />}
                  fullWidth
                  size="md"
                >
                  Add New User
                </Button>
                
                <Button 
                  variant="light" 
                  color="exportGreen"
                  onClick={() => navigate('/admin/gallery/home')}
                  rightSection={<IconArrowRight size={16} />}
                  fullWidth
                  size="md"
                >
                  Update Gallery
                </Button>
                
                <Button 
                  variant="light" 
                  color="exportGreen"
                  onClick={() => navigate('/admin/settings/general')}
                  rightSection={<IconArrowRight size={16} />}
                  fullWidth
                  size="md"
                >
                  Configure Settings
                </Button>
                
                <Button 
                  variant="light" 
                  color="exportGreen"
                  onClick={() => navigate('/admin/security/2fa')}
                  rightSection={<IconArrowRight size={16} />}
                  fullWidth
                  size="md"
                >
                  Security Settings
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          {/* System Info & Unread Inquiries */}
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" radius="md">
              <Group justify="space-between">
                <Group>
                  <IconMessageCircle size={20} className="text-gray-500" />
                  <Text size="sm" c="dimmed">Unread Inquiries</Text>
                </Group>
                <Group>
                  <Badge color={unreadInquiries > 0 ? "red" : "green"} variant="light">
                    {unreadInquiries > 0 ? `${unreadInquiries} unread` : "No unread"}
                  </Badge>
                  <Button 
                    variant="subtle" 
                    size="xs" 
                    onClick={() => navigate('/admin/inquiries')}
                  >
                    View
                  </Button>
                </Group>
              </Group>
              <Divider my="sm" />
              <Group justify="space-between">
                <Group>
                  <IconSettings size={20} className="text-gray-500" />
                  <Text size="sm" c="dimmed">System Status</Text>
                </Group>
                <Group>
                  <Badge color="green" variant="light">All Systems Operational</Badge>
                  <Text size="xs" c="dimmed">v1.0.0</Text>
                </Group>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>
    </>
  );
};